import React, { createContext, useContext, useState } from "react";

// Create the Form Metadata Context
const FormMetadataContext = createContext();

/**
 * FormMetadataProvider component that manages form metadata (title, description, privacy)
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.initialTitle - Initial form title
 * @param {string} props.initialDescription - Initial form description
 * @param {Object} props.initialPrivacy - Initial form privacy settings
 */
export const FormMetadataProvider = ({
  children,
  initialTitle = "Untitled Form",
  initialDescription = "",
  initialPrivacy = {
    value: 2,
    label: "Workspace",
    Icon: "TASK_LOCKED_OUTLINED",
  },
}) => {
  const [formTitle, setFormTitle] = useState(initialTitle);
  const [formDescription, setFormDescription] = useState(initialDescription);
  const [formPrivacy, setFormPrivacy] = useState(initialPrivacy);
  const [allowedProjectIds, setAllowedProjectIds] = useState([]); // For custom privacy

  // Context value object
  const value = {
    formTitle,
    setFormTitle,
    formDescription,
    setFormDescription,
    formPrivacy,
    setFormPrivacy,
    allowedProjectIds,
    setAllowedProjectIds,
  };

  return <FormMetadataContext.Provider value={value}>{children}</FormMetadataContext.Provider>;
};

/**
 * Custom hook to use the Form Metadata Context
 * @returns {Object} Form metadata context value
 * @throws {Error} If used outside of FormMetadataProvider
 */
export const useFormMetadata = () => {
  const context = useContext(FormMetadataContext);

  if (context === undefined) {
    throw new Error("useFormMetadata must be used within a FormMetadataProvider");
  }

  return context;
};

export default FormMetadataContext;
